import type { FeatureLauncher, FeatureLauncherRoute, FeatureSource } from '../models/control-form';
import type { FeatureSourceHeaderField } from './feature-source-prefill';
import { featureMatchesWhere, splitValues } from './feature-source-prefill';

/**
 * Map-first launcher forms (`ui_config.feature_launcher`, targets marked `ui_config.launcher_only`). Pure, so the
 * rules run under karma and match mobile: routes resolve by control_form `type`, the first matching route wins.
 */

/** The control_form keys these helpers read. */
export interface LauncherFormLike {
  id?: unknown;
  type?: string | null;
  name?: string | null;
  ui_config?: {
    feature_launcher?: FeatureLauncher | null;
    launcher_only?: boolean | null;
    /** A target form's own prefill config and header fields (read for done/pending, see feature-link-status). */
    feature_source?: FeatureSource | null;
    header?: { fields?: FeatureSourceHeaderField[] | null } | null;
  } | null;
}

/** A route whose target form exists in the project. */
export interface ResolvedLauncherRoute<F extends LauncherFormLike = LauncherFormLike> {
  when: FeatureLauncherRoute['when'];
  form: F;
}

/** The form's launcher config when it is a usable launcher (has a layer), else null. */
export function featureLauncherOf(form: LauncherFormLike | null | undefined): FeatureLauncher | null {
  const launcher = form?.ui_config?.feature_launcher;
  return launcher && typeof launcher === 'object' && typeof launcher.layer === 'string' && launcher.layer ? launcher : null;
}

export function isLauncherOnly(form: LauncherFormLike | null | undefined): boolean {
  return form?.ui_config?.launcher_only === true;
}

/**
 * The form-type choices: adding a form hides `launcher_only` forms (reached through their launcher); the form
 * currently selected stays listed so the selector can show it (a routed target, or an existing entry).
 */
export function formTypeChoices<F extends LauncherFormLike>(forms: F[] | null | undefined, adding: boolean, currentId?: unknown): F[] {
  const list = forms || [];
  if (!adding) return list;
  return list.filter(form => !isLauncherOnly(form) || (currentId !== undefined && currentId !== null && currentId !== '' && form.id === currentId));
}

/** Routes in config order whose `form_type` names a form of the project (the first form of that type). */
export function resolveLauncherRoutes<F extends LauncherFormLike>(launcher: FeatureLauncher | null | undefined,
  forms: F[] | null | undefined): ResolvedLauncherRoute<F>[] {
  const routes = Array.isArray(launcher?.routes) ? launcher.routes : [];
  const resolved: ResolvedLauncherRoute<F>[] = [];
  for (const route of routes) {
    if (!route || typeof route.form_type !== 'string' || !route.form_type) continue;
    const form = (forms || []).find(item => item?.type === route.form_type);
    if (form) resolved.push({ when: route.when ?? null, form });
  }
  return resolved;
}

/** The first route the feature matches (a route without `when` matches everything), or null. */
export function matchLauncherRoute<F extends LauncherFormLike>(routes: ResolvedLauncherRoute<F>[] | null | undefined,
  properties: Record<string, unknown> | null | undefined): ResolvedLauncherRoute<F> | null {
  return (routes || []).find(route => featureMatchesWhere(route.when || undefined, properties || {})) || null;
}

/** Chooser row text: the `label_fields` values present, joined " · "; '' when none (callers fall back to the feature label). */
export function launcherCandidateLabel(properties: Record<string, unknown> | null | undefined, labelFields: string[] | null | undefined): string {
  if (!Array.isArray(labelFields)) return '';
  return labelFields
    .map(field => properties?.[field])
    .filter(value => value !== null && value !== undefined && typeof value !== 'object' && String(value).trim() !== '')
    .map(value => String(value).trim())
    .join(' · ');
}

/** A tapped feature's properties, one entry per value of the routed multi-value fields (see expandLauncherProperties). */
export interface ExpandedLauncherProperties {
  properties: Record<string, unknown>;
  /** Distinguishes rows of the same feature ('' when not expanded). */
  variant: string;
}

/**
 * One point, several tests (Addendum 2): each value of a `multi_value_fields` property that the routes test becomes
 * its own row, with that property replaced by the single value (cartesian over such fields; in practice one).
 * Identical rows are dropped. A feature without multi values gives itself once.
 */
export function expandLauncherProperties(properties: Record<string, unknown> | null | undefined,
  launcher: { routes?: { when?: FeatureLauncherRoute['when'] }[] | null; multi_value_fields?: FeatureLauncher['multi_value_fields'] }
    | null | undefined): ExpandedLauncherProperties[] {
  const base = { ...(properties || {}) };
  const multi = launcher?.multi_value_fields;
  const routed = new Set((launcher?.routes || []).flatMap(route => Object.keys(route?.when || {})));
  const fields = multi ? Object.keys(multi).filter(field => routed.has(field) && typeof multi[field] === 'string' && multi[field]) : [];
  let rows: ExpandedLauncherProperties[] = [{ properties: base, variant: '' }];
  for (const field of fields) {
    const values = splitValues(base, field, multi);
    if (values.length < 2) continue;
    rows = rows.flatMap(row => values.map(value => ({
      properties: { ...row.properties, [field]: value },
      variant: row.variant ? `${row.variant}|${field}=${value}` : `${field}=${value}`,
    })));
  }
  const seen = new Set<string>();
  return rows.filter(row => !seen.has(row.variant) && !!seen.add(row.variant));
}
